import React from "react";
import { Link } from "react-router-dom";
import { useBusinessTimezone } from "../../context/BusinessTimezone/BusinessTimezoneContext";
import { resolveProfilePicUrl } from "../../helpers/resolveMediaUrl";
import {
	formatMessageCalendarDay,
	formatMessageClock,
	isMessageSameLocalDay,
	isMessageYesterday,
} from "../../helpers/dateTimeFormat";

function IconClock() {
	return (
		<svg
			className="size-3.5 shrink-0"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<circle cx="12" cy="12" r="10" />
			<path d="M12 6v6l4 2" />
		</svg>
	);
}

export function getLastMessagePreviewText(ticket) {
	const msg = ticket.lastMessageObj;
	const isGroup = ticket.contact?.isGroup;
	let prefix = "";

	if (msg && !msg.fromMe && isGroup) {
		prefix = `${msg.contact?.name || ""}: `;
	} else if (msg?.fromMe && isGroup) {
		prefix = "Você: ";
	}

	if (!msg) {
		return (ticket.lastMessage || "").replace(/\n/g, " ").trim();
	}

	if (msg.mediaType === "audio") return `${prefix}Áudio`;
	if (msg.mediaType === "image" || /\.(jpe?g|png)$/i.test(msg.body || "")) {
		return `${prefix}Foto`;
	}
	if ((msg.body || "").endsWith(".pdf")) return `${prefix}Documento`;
	if (msg.mediaType === "webp" || (msg.body || "").endsWith(".webp")) {
		return `${prefix}Figurinha`;
	}
	if (msg.mediaType === "vcard") return "Contato";
	if (msg.mediaType === "multi_vcard") return "Contatos";

	return `${prefix}${(msg.body || "").replace(/\n/g, " ").trim()}`;
}

function formatTicketTimeLabel(lastUpdate, businessTimeZone) {
	if (!lastUpdate) return "";
	if (isMessageSameLocalDay(lastUpdate, new Date(), businessTimeZone)) {
		return formatMessageClock(lastUpdate, businessTimeZone);
	}
	if (isMessageYesterday(lastUpdate, new Date(), businessTimeZone)) {
		return "Ontem";
	}
	return formatMessageCalendarDay(lastUpdate, businessTimeZone);
}

export default function NotificationTicketItem({ ticket, onNavigate }) {
	const { timeZone: businessTimeZone } = useBusinessTimezone();
	const lastUpdate = ticket.lastMessageObj?.createdAt || ticket.updatedAt;
	const contactName =
		ticket.channel === "email"
			? ticket.contact?.email || ""
			: ticket.contact?.name || "";
	const preview = getLastMessagePreviewText(ticket);
	const timeLabel = formatTicketTimeLabel(lastUpdate, businessTimeZone);
	const avatarUrl = resolveProfilePicUrl(ticket.contact?.profilePicUrl);
	const initial = (contactName || "?").charAt(0).toUpperCase();
	const ticketPath = ticket?.id ? `/tickets/${ticket.id}` : null;

	const handleNavigate = (event) => {
		event.stopPropagation();
		onNavigate?.();
	};

	if (!ticketPath) return null;

	return (
		<Link
			to={ticketPath}
			className="notifications-popover__item flex w-full items-start gap-3 border-0 bg-transparent p-4 text-left no-underline transition-colors hover:bg-default-150 dark:hover:bg-default-800/60"
			onClick={handleNavigate}
		>
			<div className="relative shrink-0">
				<div className="flex size-10 items-center justify-center overflow-hidden rounded-md bg-default-100 dark:bg-default-800">
					{avatarUrl ? (
						<img src={avatarUrl} alt="" className="size-full object-cover" />
					) : (
						<span className="text-sm font-semibold text-default-500">{initial}</span>
					)}
				</div>
			</div>
			<div className="flex min-w-0 flex-1 items-start justify-between gap-2 text-sm">
				<div className="min-w-0 flex-1">
					<h6 className="mb-1 truncate font-medium text-default-800 dark:text-default-100">
						{contactName}
					</h6>
					{preview ? (
						<p className="mb-0 truncate text-xs text-default-500">{preview}</p>
					) : null}
					{timeLabel ? (
						<p className="mb-0 mt-1.5 flex items-center gap-1 text-xs text-default-500">
							<IconClock />
							<span>{timeLabel}</span>
						</p>
					) : null}
				</div>
				{ticket.unreadMessages > 0 ? (
					<span
						className="mt-1 size-1.5 shrink-0 rounded-full bg-primary"
						aria-hidden
					/>
				) : null}
			</div>
		</Link>
	);
}
